<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="innerVisible"
    width="560px"
    append-to-body
    custom-class="client-download-dialog-layer"
    class="client-download-dialog"
    @close="onClose"
  >
    <div v-if="!loadingVersions && !versions.length" class="empty-package-hint">
      <i class="el-icon-box"></i>
      <p>{{ $tt("暂无安装包，敬请期待") }}</p>
    </div>
    <template v-else>
      <div v-if="showArchPicker" class="dialog-arch-row">
        <span class="arch-label">{{ $tt("CPU 架构") }}</span>
        <el-radio-group v-model="cpuArch" size="mini" @change="onTargetChange">
          <el-radio-button
            v-for="o in archOptions"
            :key="o.value"
            :label="o.value"
          >{{ $tt(o.label) }}</el-radio-button>
        </el-radio-group>
      </div>
      <p v-if="clientType === 'windows'" class="static-hint win-hint">
        {{ $tt("Windows 8～11 通用安装包") }}
      </p>

      <el-input
        v-model="versionKeyword"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        class="version-search"
        :placeholder="$tt('搜索版本号')"
      />

      <div v-loading="loadingVersions" class="version-list-wrap">
        <div class="version-list">
          <button
            v-for="v in displayVersions"
            :key="v.id"
            type="button"
            class="version-row"
            :class="{ active: versionId === v.id }"
            @click="selectVersion(v.id)"
          >
            <div class="version-row-head">
              <span class="ver-name">v{{ v.name || "—" }}</span>
              <span class="ver-tags">
                <el-tag v-if="v.status !== 'true'" size="mini" type="info" effect="plain">{{ $tt("不参与 OTA") }}</el-tag>
                <el-tag v-if="v.cpu_arch && showArchPicker" size="mini" effect="plain">{{ v.cpu_arch }}</el-tag>
              </span>
            </div>
            <p v-if="v.version_log" class="ver-log-preview">{{ v.version_log }}</p>
          </button>
        </div>
        <p v-if="!loadingVersions && versionKeyword && !displayVersions.length" class="search-empty">
          {{ $tt("未找到匹配的版本") }}
        </p>
        <button
          v-if="canExpandMore"
          type="button"
          class="expand-more-btn"
          @click="showAllVersions = true"
        >
          <i class="el-icon-arrow-down"></i>
          {{ $tt("查看更多历史版本") }}
          <span class="expand-count">（{{ collapsedHiddenCount }}）</span>
        </button>
        <button
          v-else-if="canCollapse"
          type="button"
          class="expand-more-btn collapse"
          @click="showAllVersions = false"
        >
          <i class="el-icon-arrow-up"></i>
          {{ $tt("收起") }}
        </button>
      </div>

      <div v-if="versionId && (activeVersionLog || activeRemark)" class="detail-block">
        <template v-if="activeVersionLog">
          <h4>{{ $tt("更新内容") }}</h4>
          <pre class="changelog-box">{{ activeVersionLog }}</pre>
        </template>
        <template v-if="activeRemark">
          <h4>{{ $tt("版本说明") }}</h4>
          <pre class="changelog-box muted-box">{{ activeRemark }}</pre>
        </template>
      </div>
      <p v-if="resolvedHref && !resolvedEnabled" class="hint-warn">
        {{ $tt("该版本未启用，仅作历史包下载") }}
      </p>
    </template>
    <span slot="footer">
      <el-button @click="innerVisible = false">{{ $tt("取消") }}</el-button>
      <el-button
        type="primary"
        native-type="button"
        :disabled="noPackages || !resolvedHref"
        @click.native.prevent.stop="startDownload"
      >
        <i class="el-icon-download"></i>
        {{ $tt("下载") }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  ARCH_OPTIONS,
  needsArchPicker,
  defaultArch,
  CLIENT_DOWNLOAD_ORDER,
} from "@/utils/clientPlatforms";
import {
  normalizeClientDownloadUrl,
  resolveClientPackageFileName,
  triggerClientPackageDownload,
} from "@/utils/clientDownload";

const COLLAPSED_LIMIT = 10;

export default {
  name: "ClientDownloadDialog",
  props: {
    visible: { type: Boolean, default: false },
    clientType: { type: String, default: "android" },
    initialCpuArch: { type: String, default: "" },
  },
  data() {
    return {
      innerVisible: false,
      cpuArch: "arm64",
      versions: [],
      versionId: "",
      resolvedHref: "",
      resolvedDownloadMeta: null,
      resolvedEnabled: true,
      activeVersionLog: "",
      activeRemark: "",
      loadingVersions: false,
      versionKeyword: "",
      showAllVersions: false,
    };
  },
  computed: {
    dialogTitle() {
      const row = CLIENT_DOWNLOAD_ORDER.find((c) => c.key === this.clientType);
      const name = row ? this.$tt(row.title) : this.$tt("客户端下载");
      return `${name} · ${this.$tt("版本下载")}`;
    },
    showArchPicker() {
      return needsArchPicker(this.clientType);
    },
    archOptions() {
      if (this.clientType === "kylin") return ARCH_OPTIONS.qt_kylin;
      if (this.clientType === "mac") return ARCH_OPTIONS.qt_mac;
      return [];
    },
    noPackages() {
      return !this.loadingVersions && !this.versions.length;
    },
    filteredVersions() {
      const kw = (this.versionKeyword || "").trim().toLowerCase();
      const list = this.versions || [];
      if (!kw) return list;
      return list.filter((v) => String(v.name || "").toLowerCase().includes(kw));
    },
    displayVersions() {
      const list = this.filteredVersions;
      if ((this.versionKeyword || "").trim()) {
        return list;
      }
      if (this.showAllVersions) {
        return list;
      }
      return list.slice(0, COLLAPSED_LIMIT);
    },
    canExpandMore() {
      if ((this.versionKeyword || "").trim()) return false;
      if (this.showAllVersions) return false;
      return this.filteredVersions.length > COLLAPSED_LIMIT;
    },
    canCollapse() {
      if ((this.versionKeyword || "").trim()) return false;
      return this.showAllVersions && this.versions.length > COLLAPSED_LIMIT;
    },
    collapsedHiddenCount() {
      return Math.max(0, this.filteredVersions.length - COLLAPSED_LIMIT);
    },
  },
  watch: {
    visible(v) {
      this.innerVisible = v;
      if (v) {
        this.openForType(this.clientType);
      }
    },
    innerVisible(v) {
      if (!v) {
        this.$emit("update:visible", false);
      }
    },
    initialCpuArch() {
      if (this.innerVisible && needsArchPicker(this.clientType)) {
        this.cpuArch = this.initialCpuArch || defaultArch(this.clientType);
        this.onTargetChange();
      }
    },
    clientType(t) {
      if (this.innerVisible) {
        this.openForType(t);
      }
    },
  },
  methods: {
    onClose() {
      this.$emit("update:visible", false);
    },
    openForType(type) {
      if (needsArchPicker(type)) {
        this.cpuArch = this.initialCpuArch || defaultArch(type);
      } else {
        this.cpuArch = "";
      }
      this.versionId = "";
      this.resolvedHref = "";
      this.resolvedDownloadMeta = null;
      this.activeVersionLog = "";
      this.activeRemark = "";
      this.versionKeyword = "";
      this.showAllVersions = false;
      this.loadVersions().then(() => this.pickDefaultVersion());
    },
    buildListPayload() {
      const payload = { client_type: this.clientType, list_only: true };
      if (this.showArchPicker) {
        payload.cpu_arch = this.cpuArch;
      }
      return payload;
    },
    buildResolvePayload(extra) {
      const payload = {
        client_type: this.clientType,
        ...extra,
      };
      if (this.showArchPicker) {
        payload.cpu_arch = this.cpuArch;
      }
      return payload;
    },
    syncChangelogFromList(id) {
      const row = this.versions.find((v) => v.id === id);
      if (row) {
        this.activeVersionLog = row.version_log || "";
        this.activeRemark = row.remark || "";
      }
    },
    async loadVersions() {
      this.loadingVersions = true;
      try {
        const res = await this.$request.listClientDownloadVersions(this.buildListPayload());
        if (res.status === "success") {
          const data = res.data || {};
          this.versions = data.versions || [];
        } else {
          this.versions = [];
        }
      } catch (e) {
        this.versions = [];
      } finally {
        this.loadingVersions = false;
      }
    },
    async onTargetChange() {
      this.versionId = "";
      this.resolvedHref = "";
      this.resolvedDownloadMeta = null;
      this.activeVersionLog = "";
      this.activeRemark = "";
      this.showAllVersions = false;
      await this.loadVersions();
      await this.pickDefaultVersion();
    },
    async pickDefaultVersion() {
      if (!this.versions.length) {
        return;
      }
      const enabled = this.versions.find((v) => v.status === "true");
      const pick = enabled || this.versions[0];
      await this.selectVersion(pick.id);
    },
    async selectVersion(id) {
      this.versionId = id;
      this.syncChangelogFromList(id);
      await this.resolveForVersion(id);
    },
    applyHrefFromListRow(id) {
      const row = this.versions.find((v) => String(v.id) === String(id));
      if (row && row.file_down_path) {
        this.resolvedHref = normalizeClientDownloadUrl(row.file_down_path);
        this.resolvedEnabled = row.status === "true";
        return true;
      }
      return false;
    },
    async resolveForVersion(id) {
      if (!id) {
        this.resolvedHref = "";
        return;
      }
      const hadListHref = this.applyHrefFromListRow(id);
      const res = await this.$request.resolveClientDownload(
        this.buildResolvePayload({ version_id: id })
      );
      if (res.status === "success" && res.data) {
        const href = res.data.href || "";
        if (href) {
          this.resolvedHref = normalizeClientDownloadUrl(href);
        } else if (!hadListHref) {
          this.resolvedHref = "";
        }
        this.resolvedEnabled = res.data.enabled !== false;
        if (res.data.version_log) {
          this.activeVersionLog = res.data.version_log;
        }
        if (res.data.remark) {
          this.activeRemark = res.data.remark;
        }
        this.resolvedDownloadMeta = res.data;
      } else if (!hadListHref) {
        this.resolvedHref = "";
        if (id === this.versionId) {
          this.$message.error(res.msg || this.$tt("无法解析下载地址"));
        }
      }
    },
    async startDownload() {
      if (!this.resolvedHref && !this.versionId) {
        return;
      }
      const row = this.versions.find((v) => String(v.id) === String(this.versionId));
      const fileName = resolveClientPackageFileName(
        row,
        this.resolvedDownloadMeta,
        this.resolvedHref
      );
      const payload = this.buildResolvePayload({ version_id: this.versionId });
      delete payload.list_only;
      let ok = false;
      try {
        ok = await triggerClientPackageDownload({
          getDownloadUrl: () => this.$request.getClientPackageDownloadUrl(payload),
          downloadFileApi: this.$request.downloadClientPackageFile,
          payload,
          href: this.resolvedHref,
          fileName,
        });
      } catch (e) {
        ok = false;
      }
      if (!ok) {
        this.$message.error(this.$tt("无法解析下载地址"));
        return;
      }
      window.setTimeout(() => {
        this.innerVisible = false;
        this.$emit("update:visible", false);
      }, 120);
    },
  },
};
</script>

<style scoped lang="scss">
.static-hint {
  color: #606266;
  font-size: 13px;
}
.win-hint {
  margin: 0 0 12px;
}
.dialog-arch-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
  .arch-label {
    font-size: 13px;
    color: #909399;
  }
}
.version-search {
  margin-bottom: 12px;
}
.version-list-wrap {
  min-height: 80px;
}
.version-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}
.version-row {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f5f9ff;
  }
  &.active {
    background: #ecf5ff;
    box-shadow: inset 3px 0 0 #409eff;
  }
}
.version-row-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}
.ver-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}
.ver-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
.ver-log-preview {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}
.expand-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  background: #fff;
  color: #409eff;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    border-color: #409eff;
    background: #f5f9ff;
  }
  .expand-count {
    color: #909399;
  }
}
.search-empty {
  margin: 12px 0 0;
  text-align: center;
  font-size: 13px;
  color: #909399;
}
.detail-block {
  margin-top: 14px;
  h4 {
    margin: 0 0 6px;
    font-size: 13px;
    color: #606266;
  }
}
.changelog-box {
  margin: 0 0 10px;
  max-height: 120px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
}
.muted-box {
  color: #606266;
}
.hint-warn {
  margin: 8px 0 0;
  font-size: 12px;
  color: #e6a23c;
}
.empty-package-hint {
  text-align: center;
  padding: 28px 16px 8px;
  color: #909399;
  i {
    font-size: 40px;
    color: #dcdfe6;
    display: block;
    margin-bottom: 12px;
  }
  p {
    margin: 0;
    font-size: 15px;
    color: #606266;
  }
}
</style>
